import { useMemo } from 'react';
import { surfaceAt, NO_SURFACE } from '../engine/battlefield/surfaceEffects';
import { terrainRulesFor, traitIdsOf } from '../engine/battlefield/traitRules';
import { projectDefender } from '../engine/roster/projection';

const NOTHING = {};

/**
 * What the ground under one combatant is doing to them, or NO_SURFACE (off
 * the map included). A flyer is over the mire and a burrower under it -
 * their own traits decide, through the same rules the flood fill uses.
 * `ignoresTerrain` on the TOKEN is the GM's manual override and wins over
 * the trait, exactly as it does for movement.
 */
function effectOn(combatant, token, zones, cellSize) {
  if (!token) return NO_SURFACE;
  const rules = terrainRulesFor(traitIdsOf(combatant.sheet), projectDefender(combatant)?.size);
  if (token.ignoresTerrain) rules.ignoresTerrain = true;
  return surfaceAt(token.x, token.z, zones, rules, cellSize);
}

/**
 * useSurfaceEffects - what the ground is doing to each combatant.
 *
 * One computation, two consumers: the roster projection folds the
 * effects into each combatant's sheet, and the map draws a note on their
 * marker. Computing it twice would be how the two come to disagree.
 *
 * Keyed on the tokens object and the zones, both of which the sync layer
 * replaces on change, so this recomputes exactly when somebody moves or
 * the ground is repainted. NOTHING (one shared empty object) when nobody
 * is affected, so an unchanged answer keeps its identity.
 */
export default function useSurfaceEffects({ roster, tokens, zones, cellSize = 1, enabled = true }) {
  return useMemo(() => {
    if (!enabled || !zones?.length || !roster?.length) return NOTHING;
    const out = {};
    for (const combatant of roster) {
      const effect = effectOn(combatant, tokens?.[combatant.id], zones, cellSize);
      if (effect !== NO_SURFACE) out[combatant.id] = effect;
    }
    return Object.keys(out).length ? out : NOTHING;
  }, [roster, tokens, zones, cellSize, enabled]);
}
